import { useState } from 'react';
import { connect } from 'react-redux';
import toast from 'react-hot-toast';
import { ChevronLeft, Lock, Plus } from 'lucide-react';
import Header from 'components/Profile/Header';
import ViewFooter from 'components/Profile/View/Footer';
import { ProfileActions } from 'store/actions';
import { getStepById } from 'pages/Profile/helper';
import Item from './Item';
import Tabs from './Tabs';
import AuthorizePersonModal from './Modal';
import { TABS, EMPTY_STATES } from './helper';

const enhancer = connect(null, { ...ProfileActions });

const Authorization = ({ contacts, revokeAuthorizedContact, reAuthorizeContact, onRefresh = null, onBack }) => {
  const step = getStepById('consent');
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.AUTHORIZED);
  const [confirmId, setConfirmId] = useState(null);

  const { authorized = [], revoked = [], family_members, emergency_contacts } = contacts || {};
  const isRevokedTab = activeTab === TABS.REVOKED;
  const list = isRevokedTab ? revoked : authorized;
  const { icon: EmptyIcon, iconClassName, title, description } = EMPTY_STATES[activeTab];

  const handleAction = async (id) => {
    try {
      await (isRevokedTab ? reAuthorizeContact(id) : revokeAuthorizedContact(id));

      toast.success(isRevokedTab ? 'Access restored' : 'Access revoked');
      setConfirmId(null);
      onRefresh?.();
    } catch (error: any) {
      toast.error(error.errors || 'Something went wrong. Please try again later.');
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 px-5 pt-5 lg:px-10">
        <div className="mb-5">
          <Header
            icon={step.icon}
            iconBg={step.iconBg}
            iconColor={step.iconColor}
            title={step.title}
            description="Authorizations · who can access your info"
            action={
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                aria-label="Add person"
                className="btn h-10 min-h-0 gap-1.5 rounded-xl border-none bg-teamcare-blue px-3 font-semibold text-white hover:bg-teamcare-blue-dark sm:px-4"
              >
                <Plus size={18} strokeWidth={2.5} />
                <span className="hidden sm:inline">Add person</span>
              </button>
            }
          />
        </div>

        <button
          type="button"
          onClick={onBack}
          className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-secondary-600 transition-colors hover:text-secondary-900"
        >
          <ChevronLeft size={16} strokeWidth={2.5} /> All consents
        </button>

        <Tabs
          activeTab={activeTab}
          counts={{ [TABS.AUTHORIZED]: authorized.length, [TABS.REVOKED]: revoked.length }}
          onTabChange={setActiveTab}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-7 lg:px-10">
        <div key={activeTab} className="animate-slide-up">
          {list.length ? (
            <div className="divide-y divide-gray-100">
              {list.map((contact) => (
                <Item
                  key={contact.id}
                  contact={contact}
                  revoked={isRevokedTab}
                  confirming={confirmId === contact.id}
                  onAction={() => setConfirmId(contact.id)}
                  onCancel={() => setConfirmId(null)}
                  onConfirm={() => handleAction(contact.id)}
                />
              ))}
            </div>
          ) : (
            <div className="mt-4 flex flex-col items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-secondary-50/50 px-6 py-10 text-center">
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${iconClassName}`}>
                <EmptyIcon size={22} />
              </span>
              <p className="text-sm font-bold text-secondary-900">{title}</p>
              <p className="max-w-md text-xs text-secondary-500">{description}</p>
            </div>
          )}

          <p className="mt-4 flex items-start gap-2 text-xs text-secondary-400">
            <Lock size={14} className="mt-0.5 shrink-0" />
            Staff verify the caller's identity before sharing anything. Changes take effect immediately and are recorded in your patient file.
          </p>
        </div>
      </div>

      <ViewFooter stepId="consent" />

      {modalOpen && (
        <AuthorizePersonModal
          contacts={{ family_members, emergency_contacts }}
          onClose={() => setModalOpen(false)}
          onAuthorized={onRefresh}
        />
      )}
    </div>
  );
};

export default enhancer(Authorization);
